import { useState, useEffect, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Mail,
    Save,
    Loader2,
    Eye,
    ChevronLeft,
    Sparkles,
    UserPlus,
    CreditCard,
    AlertTriangle,
    CalendarClock,
    Megaphone
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface EmailTemplate {
    id: string;
    template_type: string;
    subject: string;
    html_content: string;
    is_enabled: boolean;
    updated_at: string;
}

const DEFAULT_TEMPLATES: Record<string, { subject: string; content: string }> = {
    welcome: {
        subject: "🎉 ยินดีต้อนรับสู่ GenCNX!",
        content: `
            <h2 style="color: #6366f1;">ยินดีต้อนรับสู่ GenCNX! 🚀</h2>
            <p>สวัสดีครับ {{display_name}},</p>
            <p>ขอบคุณที่สมัครใช้งาน GenCNX! เราพร้อมช่วยคุณสร้างภาพ วิดีโอ และเพลง AI คุณภาพสูงแล้ว</p>
            <p>คุณได้รับ <strong>100 เครดิต</strong> ฟรีสำหรับเริ่มต้นใช้งาน</p>
            <p><a href="https://www.gencnx.com/generate" style="display: inline-block; background: #6366f1; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px;">เริ่มสร้างผลงานเลย!</a></p>
            <p>หากมีข้อสงสัยใดๆ สามารถติดต่อเราได้ตลอดเวลาครับ</p>
        `
    },
    payment: {
        subject: "✅ การชำระเงินสำเร็จ - GenCNX",
        content: `
            <h2 style="color: #22c55e;">การชำระเงินสำเร็จ! ✅</h2>
            <p>สวัสดีครับ {{display_name}},</p>
            <p>เราได้รับการชำระเงินของคุณเรียบร้อยแล้ว</p>
            <div style="background: #f3f4f6; padding: 16px; border-radius: 8px; margin: 16px 0;">
                <p><strong>รายละเอียด:</strong></p>
                <ul>
                    <li>จำนวนเงิน: ฿{{amount}}</li>
                    <li>เครดิตที่ได้รับ: {{credits}}</li>
                    <li>เครดิตคงเหลือ: {{total_credits}}</li>
                </ul>
            </div>
            <p><a href="https://www.gencnx.com/generate" style="display: inline-block; background: #22c55e; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px;">ใช้เครดิตสร้างผลงาน</a></p>
        `
    },
    low_credit: {
        subject: "⚠️ เครดิตของคุณใกล้หมดแล้ว!",
        content: `
            <h2 style="color: #f59e0b;">เครดิตใกล้หมดแล้ว! ⚠️</h2>
            <p>สวัสดีครับ {{display_name}},</p>
            <p>เครดิตของคุณเหลือเพียง <strong>{{remaining_credits}} เครดิต</strong></p>
            <p>หากต้องการใช้งานต่อ สามารถซื้อเครดิตเพิ่มได้ที่ปุ่มด้านล่างนี้ครับ</p>
            <p><a href="https://www.gencnx.com/pricing" style="display: inline-block; background: #f59e0b; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px;">ซื้อเครดิตเพิ่ม</a></p>
        `
    },
    monthly: {
        subject: "📊 สรุปการใช้งานประจำเดือน - GenCNX",
        content: `
            <h2 style="color: #6366f1;">สรุปการใช้งานประจำเดือน 📊</h2>
            <p>สวัสดีครับ {{display_name}},</p>
            <p>นี่คือสรุปการใช้งาน GenCNX ของคุณในเดือนที่ผ่านมา:</p>
            <div style="background: #f3f4f6; padding: 16px; border-radius: 8px; margin: 16px 0;">
                <ul>
                    <li>ภาพที่สร้าง: {{photo_count}} ภาพ</li>
                    <li>วิดีโอที่สร้าง: {{video_count}} คลิป</li>
                    <li>เพลงที่สร้าง: {{music_count}} เพลง</li>
                    <li>เครดิตที่ใช้ไป: {{credits_used}}</li>
                    <li>เครดิตคงเหลือ: {{remaining_credits}}</li>
                </ul>
            </div>
            <p><a href="https://www.gencnx.com/history" style="display: inline-block; background: #6366f1; color: white; padding: 12px 24px; text-decoration: none; border-radius: 8px;">ดูประวัติการสร้าง</a></p>
        `
    },
    promotion: {
        subject: "🎁 โปรโมชั่นพิเศษสำหรับคุณ! - GenCNX",
        content: `
            <h2 style="color: #ec4899;">สวัสดีครับ {{display_name}}! 🎁</h2>
            <p>ลองใช้งาน GenCNX แล้วเป็นอย่างไรบ้างครับ? ชอบไหมครับ</p>
            <p>หากคุณชอบและต้องการใช้งานต่อ เรามีโปรโมชั่นพิเศษมาแนะนำครับ!</p>

            <div style="background: linear-gradient(135deg, #6366f1 0%, #ec4899 100%); padding: 24px; border-radius: 12px; margin: 20px 0; color: white; text-align: center;">
                <p style="font-size: 14px; margin: 0; opacity: 0.9;">โปรโมชั่นพิเศษ</p>
                <p style="font-size: 32px; font-weight: bold; margin: 8px 0;">3,000 เครดิต</p>
                <p style="font-size: 24px; margin: 0;">เพียง <strong>999 บาท</strong> <span style="font-size: 14px;">(รวม VAT แล้ว)</span></p>
            </div>

            <div style="background: #f3f4f6; padding: 16px; border-radius: 8px; margin: 16px 0;">
                <p style="margin: 0 0 8px 0;"><strong>✅ สิทธิพิเศษ:</strong></p>
                <ul style="margin: 0; padding-left: 20px;">
                    <li>ราคาประหยัดกว่าปกติ</li>
                    <li>ออกใบกำกับภาษีได้</li>
                    <li>เครดิตไม่มีวันหมดอายุ</li>
                </ul>
            </div>

            <p style="text-align: center;">
                <a href="https://www.gencnx.com/pricing" style="display: inline-block; background: linear-gradient(135deg, #6366f1 0%, #ec4899 100%); color: white; padding: 14px 32px; text-decoration: none; border-radius: 8px; font-weight: bold; font-size: 16px;">🛒 ดูแพ็คเกจทั้งหมด</a>
            </p>

            <p style="color: #6b7280; font-size: 14px; margin-top: 24px;">
                หากมีข้อสงสัยหรือต้องการความช่วยเหลือ สามารถติดต่อเราได้ตลอดเวลาครับ<br>
                ขอบคุณที่ใช้บริการ GenCNX 🙏
            </p>
        `
    }
};

const TEMPLATE_INFO: Record<string, { label: string; icon: React.ElementType; description: string }> = {
    welcome: {
        label: "Welcome Email",
        icon: UserPlus,
        description: "ส่งอัตโนมัติเมื่อผู้ใช้สมัครใหม่"
    },
    payment: {
        label: "Payment Confirmation",
        icon: CreditCard,
        description: "ส่งเมื่อการชำระเงินสำเร็จ"
    },
    low_credit: {
        label: "Low Credit Alert",
        icon: AlertTriangle,
        description: "ส่งเมื่อเครดิตเหลือน้อยกว่า 10"
    },
    monthly: {
        label: "Monthly Summary",
        icon: CalendarClock,
        description: "ส่งทุกต้นเดือน (รายงานสรุป)"
    },
    promotion: {
        label: "Promotion Email",
        icon: Megaphone,
        description: "โปรโมชั่นพิเศษ ส่งด้วยตนเอง"
    }
};

const EmailTemplates = () => {
    const { toast } = useToast();
    const [activeTemplate, setActiveTemplate] = useState("welcome");
    const [templates, setTemplates] = useState<Record<string, EmailTemplate>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(false);

    // Form state
    const [subject, setSubject] = useState("");
    const [htmlContent, setHtmlContent] = useState("");
    const [isEnabled, setIsEnabled] = useState(false);

    // Quill modules
    const quillModules = useMemo(() => ({
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["link", "image"],
            ["blockquote", "code-block"],
            ["clean"],
        ],
    }), []);

    const quillFormats = [
        "header", "bold", "italic", "underline", "strike",
        "color", "background", "list", "bullet", "align",
        "link", "image", "blockquote", "code-block"
    ];

    // Fetch templates
    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const { data, error } = await (supabase as any)
                .from("email_templates")
                .select("*");

            if (error) throw error;

            const templatesMap: Record<string, EmailTemplate> = {};
            (data || []).forEach((t: EmailTemplate) => {
                templatesMap[t.template_type] = t;
            });

            setTemplates(templatesMap);

            // Load first template
            loadTemplate("welcome", templatesMap);
        } catch (error: any) {
            console.error("Error fetching templates:", error);
        } finally {
            setLoading(false);
        }
    };

    // Load template into form
    const loadTemplate = (type: string, templatesData?: Record<string, EmailTemplate>) => {
        const data = templatesData || templates;
        const template = data[type];

        if (template) {
            setSubject(template.subject);
            setHtmlContent(template.html_content);
            setIsEnabled(template.is_enabled);
        } else {
            // Load default
            const defaultTemplate = DEFAULT_TEMPLATES[type];
            setSubject(defaultTemplate?.subject || "");
            setHtmlContent(defaultTemplate?.content || "");
            setIsEnabled(false);
        }
    };

    // Save template
    const saveTemplate = async () => {
        setSaving(true);
        try {
            const existing = templates[activeTemplate];

            if (existing) {
                // Update
                const { error } = await (supabase as any)
                    .from("email_templates")
                    .update({
                        subject,
                        html_content: htmlContent,
                        is_enabled: isEnabled,
                        updated_at: new Date().toISOString()
                    })
                    .eq("id", existing.id);

                if (error) throw error;
            } else {
                // Create
                const { error } = await (supabase as any)
                    .from("email_templates")
                    .insert({
                        template_type: activeTemplate,
                        subject,
                        html_content: htmlContent,
                        is_enabled: isEnabled
                    });

                if (error) throw error;
            }

            toast({
                title: "Template Saved",
                description: `${TEMPLATE_INFO[activeTemplate]?.label} has been saved successfully.`
            });

            fetchTemplates();
        } catch (error: any) {
            console.error("Error saving template:", error);
            toast({
                title: "Error",
                description: "Failed to save template",
                variant: "destructive"
            });
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    // Handle tab change
    const handleTabChange = (value: string) => {
        setActiveTemplate(value);
        loadTemplate(value);
        setShowPreview(false);
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const currentInfo = TEMPLATE_INFO[activeTemplate];
    const IconComponent = currentInfo?.icon || Mail;

    return (
        <div className="space-y-6">
            <Card variant="glass">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Mail className="h-5 w-5 text-primary" />
                        Email Templates
                    </CardTitle>
                    <CardDescription>
                        Customize automated emails sent by the system
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Tabs value={activeTemplate} onValueChange={handleTabChange}>
                        <TabsList className="mb-6 grid grid-cols-2 md:grid-cols-5 w-full">
                            {Object.entries(TEMPLATE_INFO).map(([key, info]) => (
                                <TabsTrigger key={key} value={key} className="gap-2">
                                    <info.icon className="h-4 w-4" />
                                    <span className="hidden sm:inline">{info.label}</span>
                                </TabsTrigger>
                            ))}
                        </TabsList>

                        {Object.keys(TEMPLATE_INFO).map((key) => (
                            <TabsContent key={key} value={key} className="space-y-6">
                                {/* Template Info Header */}
                                <div className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-white/10">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                            <IconComponent className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <h3 className="font-medium">{currentInfo?.label}</h3>
                                            <p className="text-sm text-muted-foreground">{currentInfo?.description}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Badge className={isEnabled ? "bg-green-500/10 text-green-500" : "bg-gray-500/10 text-gray-500"}>
                                            {isEnabled ? "ENABLED" : "DISABLED"}
                                        </Badge>
                                        <Button
                                            variant={isEnabled ? "outline" : "default"}
                                            size="sm"
                                            onClick={() => setIsEnabled(!isEnabled)}
                                        >
                                            {isEnabled ? "Disable" : "Enable"}
                                        </Button>
                                    </div>
                                </div>

                                {/* Subject */}
                                <div className="space-y-2">
                                    <Label>Email Subject</Label>
                                    <Input
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder="Enter email subject..."
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Use variables like {"{{display_name}}"}, {"{{credits}}"}, etc.
                                    </p>
                                </div>

                                {/* Content Editor / Preview */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label>Email Content</Label>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setShowPreview(!showPreview)}
                                            className="gap-2"
                                        >
                                            <Eye className="h-4 w-4" />
                                            {showPreview ? "Edit" : "Preview"}
                                        </Button>
                                    </div>

                                    {showPreview ? (
                                        <div className="p-6 rounded-lg bg-white text-gray-800 min-h-[400px] border">
                                            <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
                                        </div>
                                    ) : (
                                        <div className="bg-white rounded-lg overflow-hidden">
                                            <ReactQuill
                                                theme="snow"
                                                value={htmlContent}
                                                onChange={setHtmlContent}
                                                modules={quillModules}
                                                formats={quillFormats}
                                                className="min-h-[400px]"
                                                placeholder="Design your email content..."
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Variable Reference */}
                                <div className="p-4 rounded-xl bg-muted/30 border border-white/10">
                                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                                        <Sparkles className="h-4 w-4" />
                                        Available Variables
                                    </h4>
                                    <div className="flex flex-wrap gap-2">
                                        {["{{display_name}}", "{{email}}", "{{credits}}", "{{remaining_credits}}", "{{amount}}"].map((v) => (
                                            <code key={v} className="px-2 py-1 rounded bg-primary/10 text-primary text-xs">
                                                {v}
                                            </code>
                                        ))}
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex justify-end gap-2">
                                    <Button
                                        variant="outline"
                                        onClick={() => loadTemplate(activeTemplate)}
                                    >
                                        Reset to Default
                                    </Button>
                                    <Button onClick={saveTemplate} disabled={saving}>
                                        {saving ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Save className="h-4 w-4 mr-2" />
                                        )}
                                        Save Template
                                    </Button>
                                </div>
                            </TabsContent>
                        ))}
                    </Tabs>
                </CardContent>
            </Card>
        </div>
    );
};

export default EmailTemplates;
